import React, { Suspense, lazy, useEffect, useState } from 'react';
import { RESEARCH_TYPES } from '../../utils/sosaSlots.ts';
import { Loader2, FolderKanban, ShieldAlert } from 'lucide-react';
import { useSessionStore } from '../../stores/useSessionStore.ts';
import { useProjectStore } from '../../stores/useProjectStore.ts';
import { useDataStore } from '../../stores/useDataStore.ts';
import { useEditorStore } from '../../stores/useEditorStore.ts';
import { useKoppelStore } from '../../stores/useKoppelStore.ts';
import type { RelatieItem } from '../../services/api/relationships.ts';
import { useUIStore } from '../../stores/useUIStore.ts';
import { useOnboardingStore } from '../../stores/useOnboardingStore.ts';
import { useOrganizationStore } from '../../stores/useOrganizationStore.ts';
import { Modal } from '../ui/Modal.tsx';
import ProjectSwitcher from '../shared/ProjectSwitcher.tsx';
import WhatsNewModal from '../modals/WhatsNewModal.tsx';
import OnboardingWizard from '../onboarding/OnboardingWizard.tsx';
import OnboardingChecklist from '../onboarding/OnboardingChecklist.tsx';
import CookieConsent from '../shared/CookieConsent.tsx';
import MobileWarning from '../shared/MobileWarning.tsx';
import { useTranslation } from '../../i18n.ts';
import { ApiService } from '../../services/ApiService.ts';
import type { Person, Relationship, Source, Project } from '../../types.ts';

const PersonEditor = lazy(() => import('../shared/PersonEditor.tsx'));
const KoppelVenster = lazy(() => import('../shared/KoppelVenster.tsx'));

interface AppModalsProps {
  isViewer: boolean;
  createProjectLoading: boolean;
  showInactivityWarning: boolean;
  remainingSeconds: number;
  // Callbacks
  onCloseEditor: () => void;
  onSavePerson: (
    data: any,
    opts?: { clientId?: string; relationships?: RelatieItem[]; zelfMelden?: boolean }
  ) => Promise<any>;
  onDeletePerson: (personId: string) => Promise<void>;
  onSetProband: (personId: string, personName?: string) => void;
  onRefreshData: () => Promise<void>;
  onDismissInactivity: () => void;
  onCloseSwitcher: () => void;
  onSwitcherSelect: (p: Project) => void;
  onSwitcherCreate: () => void;
  onSwitcherImport: () => void;
  onProjectDeleted: () => void;
  onProjectUpdated: (updated: Partial<Project> & { id: string }) => void;
  onInvitationHandled: () => void;
  onCreateProject: (
    name: string,
    description: string,
    researchType: string,
    organizationId?: string | null,
    extras?: {
      generations?: number | null;
      startDate?: string | null;
      expectedDeliveryDate?: string | null;
    }
  ) => void;
  broadcastChange: (type: string, data?: any) => void;
}

const AppModals: React.FC<AppModalsProps> = ({
  isViewer,
  createProjectLoading,
  showInactivityWarning,
  remainingSeconds,
  onCloseEditor,
  onSavePerson,
  onDeletePerson,
  onSetProband,
  onRefreshData,
  onDismissInactivity,
  onCloseSwitcher,
  onSwitcherSelect,
  onSwitcherCreate,
  onSwitcherImport,
  onProjectDeleted,
  onProjectUpdated,
  onInvitationHandled,
  onCreateProject,
  broadcastChange,
}) => {
  const { t, lang } = useTranslation();

  // ── Zustand stores ──
  const { pendingInvitations } = useSessionStore();
  const { currentProject, projects } = useProjectStore();
  const { people, relationships, sources } = useDataStore();
  const rootId = useProjectStore((s) => s.rootId);
  const {
    isOpen: isEditorOpen,
    person: editingPerson,
    initialGender: editorInitialGender,
    initialTab: editorInitialTab,
    initialSelectedEventId: editorInitialSelectedEventId,
    sessionId: editorSessionId,
    initialPlek: editorInitialPlek,
    initialVoorinvulling: editorInitialVoorinvulling,
    initialClientId: editorInitialClientId,
  } = useEditorStore();
  const koppelOpties = useKoppelStore((s) => s.opties);
  const koppelSessie = useKoppelStore((s) => s.sessie);
  const {
    showCreateProjectModal,
    setShowCreateProjectModal,
    showProjectSwitcher,
    projectSwitcherInitialTab,
    setProjectSwitcherInitialTab,
    showWhatsNew,
    setShowWhatsNew,
    whatsNewData,
    setWhatsNewData,
  } = useUIStore();

  const showWizard = useOnboardingStore((s) => s.showWizard);
  const onboardingLoaded = useOnboardingStore((s) => s.loaded);
  const onboardingShowChecklist = useOnboardingStore((s) => s.showChecklist);
  const onboardingState = useOnboardingStore((s) => s.state);
  const skipWizard = useOnboardingStore((s) => s.skipWizard);

  // Plan 3 (#778) — organisatie-toggle in NewProjectModal. Default ON
  // (Model A werkgever per spec sectie 9). Reset bij elke opening.
  const currentOrg = useOrganizationStore((s) => s.currentOrg);
  const [attachToOrg, setAttachToOrg] = useState<boolean>(true);
  // Leden van een organisatie die dit heeft aangezet, maken altijd
  // organisatieprojecten; alleen de eigenaar mag persoonlijk werk hebben.
  const membersOrgOnly =
    currentOrg?.members_org_projects_only === true &&
    (currentOrg?.role ?? currentOrg?.memberRole) !== 'owner';
  useEffect(() => {
    if (showCreateProjectModal) setAttachToOrg(!!currentOrg);
  }, [showCreateProjectModal, currentOrg]);

  // #658 — skip wizard voor users die al projects hebben (invitees of bestaande accounts).
  // Zonder deze guard opent de wizard ook voor uitgenodigde teamleden en dwingt ze een ghost-project te maken.
  useEffect(() => {
    if (showWizard && onboardingLoaded && projects.length > 0) {
      skipWizard();
    }
  }, [showWizard, onboardingLoaded, projects.length, skipWizard]);

  return (
    <>
      {/* Onboarding Checklist — floating widget, shown on every page after wizard */}
      {onboardingLoaded &&
        onboardingShowChecklist &&
        (onboardingState.wizardCompleted || onboardingState.wizardSkipped) && (
          <OnboardingChecklist />
        )}
      {isEditorOpen && (
        <Suspense fallback={null}>
          <PersonEditor
            // #1825 — één editor per persoon: elke openEditor een vers formulier.
            key={editorSessionId}
            person={editingPerson || undefined}
            sources={sources}
            projectId={currentProject?.id}
            initialGender={editorInitialGender}
            initialTab={
              (editorInitialTab as
                | 'basic'
                | 'events'
                | 'facts'
                | 'stories'
                | 'archives'
                | 'vondsten'
                | 'media'
                | 'history'
                | undefined) || undefined
            }
            initialSelectedEventId={editorInitialSelectedEventId}
            initialPlek={editorInitialPlek}
            initialVoorinvulling={editorInitialVoorinvulling}
            initialClientId={editorInitialClientId}
            relationships={relationships}
            people={people}
            isViewer={isViewer}
            onClose={onCloseEditor}
            onSourceCreated={(s) =>
              useDataStore.getState().setSources([...useDataStore.getState().sources, s])
            }
            onSave={onSavePerson}
            isProband={editingPerson?.id === rootId}
            onSetProband={onSetProband}
            onDelete={async (personId) => {
              await onDeletePerson(personId);
              onCloseEditor();
            }}
            onRefreshData={onRefreshData}
          />
        </Suspense>
      )}
      {/* #1825 deel 2 — koppelvenster, ná de editor zodat het erboven ligt. */}
      {koppelOpties && (
        <Suspense fallback={null}>
          <KoppelVenster
            key={koppelSessie}
            opties={koppelOpties}
            people={people}
            relationships={relationships}
            projectId={currentProject?.id ?? ''}
            probandId={currentProject?.proband_id || ''}
            onSluit={useKoppelStore.getState().sluit}
            onVerversen={onRefreshData}
            broadcastChange={broadcastChange}
          />
        </Suspense>
      )}
      {showWhatsNew && whatsNewData && (
        <WhatsNewModal
          version={whatsNewData.version}
          releaseNotes={whatsNewData.body}
          onClose={() => {
            setShowWhatsNew(false);
            setWhatsNewData(null);
          }}
        />
      )}
      {/* Onboarding Wizard — alleen voor nieuwe users zonder projecten (Refs #658) */}
      {showWizard && projects.length === 0 && <OnboardingWizard t={t} language={lang} />}
      <Modal
        isOpen={showInactivityWarning}
        onClose={onDismissInactivity}
        titleId="inactivity-title"
        size="sm"
        persistent
      >
        <div className="bg-pt-forest p-8 text-center">
          <ShieldAlert className="text-pt-gold mx-auto mb-3" size={32} />
          <h2 id="inactivity-title" className="text-white font-black text-lg">
            {t('inactivityTitle')}
          </h2>
        </div>
        <div className="p-8 text-center">
          <p className="text-pt-sage-text text-sm">{t('inactivityMessage')}</p>
          <p className="text-pt-forest font-black text-3xl mt-4">
            {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
          </p>
        </div>
        <div className="p-6 border-t border-pt-forest/5 flex justify-center">
          <button
            onClick={() => {
              onDismissInactivity();
              ApiService.tryRefreshToken().catch((err) =>
                console.warn('token refresh (stay logged in) failed:', err.message || err)
              );
            }}
            className="px-8 py-3 bg-pt-forest text-pt-gold font-black rounded-full hover:bg-pt-forest/90 transition-all text-sm uppercase tracking-wider"
          >
            {t('inactivityStayLoggedIn')}
          </button>
        </div>
      </Modal>
      {/* ── Project Switcher Modal ── */}
      <ProjectSwitcher
        isOpen={showProjectSwitcher}
        onClose={() => {
          // #311: reset de initiële tab zodat een volgende open via menu niet
          // per ongeluk in de Prullenbak-tab landt.
          setProjectSwitcherInitialTab(null);
          onCloseSwitcher();
        }}
        projects={projects}
        currentProject={currentProject}
        onSelectProject={onSwitcherSelect}
        onCreateProject={onSwitcherCreate}
        onImportGedcom={onSwitcherImport}
        onProjectDeleted={onProjectDeleted}
        onProjectUpdated={onProjectUpdated}
        pendingInvitations={pendingInvitations}
        onInvitationHandled={onInvitationHandled}
        initialTab={projectSwitcherInitialTab || undefined}
      />
      {/* ── Create Project Modal ── */}
      <Modal
        isOpen={showCreateProjectModal}
        onClose={() => setShowCreateProjectModal(false)}
        titleId="create-project-title"
        size="md"
      >
        <div className="bg-pt-forest p-8">
          <h2
            id="create-project-title"
            className="text-pt-gold font-black text-lg uppercase tracking-widest flex items-center gap-3"
          >
            <FolderKanban size={20} /> {t('newProject')}
          </h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const name = ((fd.get('name') as string) || '').trim();
            if (!name) return;
            const generationsStr = ((fd.get('generations') as string) || '').trim();
            const startDate = ((fd.get('startDate') as string) || '').trim();
            const expectedDeliveryDate = ((fd.get('expectedDeliveryDate') as string) || '').trim();
            onCreateProject(
              name,
              (fd.get('description') as string) || '',
              (fd.get('researchType') as string) || '',
              currentOrg && (attachToOrg || membersOrgOnly) ? currentOrg.id : null,
              {
                generations: generationsStr ? Number(generationsStr) : null,
                startDate: startDate || null,
                expectedDeliveryDate: expectedDeliveryDate || null,
              }
            );
          }}
          className="p-8 space-y-6"
        >
          <div className="space-y-2">
            <label
              htmlFor="cp-name"
              className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
            >
              {t('projectName')} *
            </label>
            <input
              id="cp-name"
              name="name"
              required
              aria-required="true"
              autoFocus
              className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none focus:ring-2 focus:ring-pt-gold/20"
              placeholder={t('projectName')}
            />
          </div>
          <div className="space-y-2">
            <label
              htmlFor="cp-type"
              className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
            >
              {t('researchType')}
            </label>
            <select
              id="cp-type"
              name="researchType"
              defaultValue="kwartierstaat"
              className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none"
            >
              {RESEARCH_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {t(`researchType_${rt}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="cp-generations"
                className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
              >
                {t('projectGenerations')}
              </label>
              <input
                id="cp-generations"
                name="generations"
                type="number"
                min={1}
                defaultValue={5}
                className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none focus:ring-2 focus:ring-pt-gold/20"
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="cp-start-date"
                className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
              >
                {t('projectStartDate')}
              </label>
              <input
                id="cp-start-date"
                name="startDate"
                type="date"
                className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none focus:ring-2 focus:ring-pt-gold/20"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="cp-expected-delivery"
              className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
            >
              {t('projectExpectedDelivery')}
            </label>
            <input
              id="cp-expected-delivery"
              name="expectedDeliveryDate"
              type="date"
              className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none focus:ring-2 focus:ring-pt-gold/20"
            />
          </div>
          {/* Plan 3 (#778) — toggle "Project van {org}" alleen voor org-leden.
              Heeft de organisatie ingesteld dat leden alleen in organisatie-
              projecten werken, dan is er niets te kiezen: het vinkje verdwijnt
              en de server weigert een persoonlijk project ook. De eigenaar
              houdt de keuze wel. */}
          {currentOrg && !membersOrgOnly && (
            <div className="space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={attachToOrg}
                  onChange={(e) => setAttachToOrg(e.target.checked)}
                  className="h-4 w-4 accent-pt-gold"
                />
                <span className="text-sm font-bold text-pt-forest">
                  {t('organizationProjectFromOrg').replace('{orgName}', currentOrg.name)}
                </span>
              </label>
              <p className="text-[11px] text-pt-sage-text ml-7">
                {attachToOrg
                  ? t('organizationProjectFromOrgHelp')
                  : t('organizationProjectPersonalHelp')}
              </p>
            </div>
          )}
          <div className="space-y-2">
            <label
              htmlFor="cp-desc"
              className="text-[10px] font-black text-pt-sage-text uppercase tracking-widest"
            >
              {t('projectDescription')}
            </label>
            <textarea
              id="cp-desc"
              name="description"
              rows={3}
              className="w-full px-4 py-3 bg-pt-cream border border-pt-forest/5 rounded-xl text-sm font-bold text-pt-forest outline-none focus:ring-2 focus:ring-pt-gold/20 resize-none"
              placeholder={t('projectDescription')}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={createProjectLoading}
              className="flex-1 px-8 py-3 bg-pt-forest text-pt-gold rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {createProjectLoading && <Loader2 size={14} className="animate-spin" />}
              {t('createProject')}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateProjectModal(false)}
              className="px-8 py-3 bg-pt-cream text-pt-sage-text rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-pt-cream/80 transition-all"
            >
              {t('cancel')}
            </button>
          </div>
        </form>
      </Modal>
      {!showWizard && <CookieConsent />}
      <MobileWarning />
    </>
  );
};

export default AppModals;
